import {
  Building2,
  Crown,
  Flame,
  Gem,
  GraduationCap,
  Landmark,
  Layers,
  MessageSquare,
  ScrollText,
  Sparkles,
  Users,
  Utensils,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export const CALENDLY_URL =
  'https://calendly.com/ashsingh/kingdoms-of-punjab-founding-member-call'

export const TRAILER_EMBED_URL =
  'https://www.youtube.com/embed/2yMWvJinVgk?rel=0&modestbranding=1'

export type ProofPoint = {
  value: string
  label: string
}

export const proofPoints: ProofPoint[] = [
  {
    value: '65M+',
    label: 'People reached through SikhiWiki and Turbanizer',
  },
  {
    value: '120',
    label: 'Guests per evening — intimate by design',
  },
  {
    value: '60 min',
    label: 'Cinematic walk-through of the Lahore Darbar',
  },
  {
    value: '2026',
    label: 'Premiere season at Illuminarium Toronto',
  },
]

export type Pillar = {
  title: string
  body: string
  icon: LucideIcon
}

export const experiencePillars: Pillar[] = [
  {
    title: 'A court you walk into',
    body: 'Sixty minutes inside the Lahore Darbar of Maharaja Ranjit Singh — projection, scent, score, and live performance across the full Illuminarium volume.',
    icon: Landmark,
  },
  {
    title: 'A table that tells the story',
    body: 'A seated culinary chapter built from the kitchens of 19th-century Punjab, served to 120 guests a night rather than a thousand.',
    icon: Utensils,
  },
  {
    title: 'Scholarship, not pastiche',
    body: 'Built with the family legacy behind Arts of the Sikh Kingdoms and sevadars from the Sikh Art & Culture Gallery at the ROM.',
    icon: ScrollText,
  },
  {
    title: 'A pipeline, not one night',
    body: 'School programming, curriculum partners, and sponsored buses so Punjabi kids in the GTA see their history at the scale it deserves.',
    icon: GraduationCap,
  },
]

export type TreasuryFeature = {
  title: string
  body: string
  icon: LucideIcon
}

export const treasuryFeatures: TreasuryFeature[] = [
  {
    title: 'Founders’ Edition artifacts',
    body: 'Every card is a real object, figure, or place from the Sikh Kingdoms — researched, illustrated, and issued in numbered editions.',
    icon: Gem,
  },
  {
    title: 'A living Sangat',
    body: 'Members collect, trade, and complete collections together. The Treasury is how the audience stays in the story between seasons.',
    icon: Users,
  },
  {
    title: 'Status that unlocks the room',
    body: 'Court rank carries into the venue: preview nights, private Court Sessions, and real-world unlocks scanned on site.',
    icon: Crown,
  },
  {
    title: 'A permanent digital archive',
    body: 'What we build outlives the run. The Treasury becomes the most complete popular archive of the Sikh Kingdoms in the world.',
    icon: Layers,
  },
]

export type Credential = {
  label: string
  detail: string
  icon: LucideIcon
}

export const founderCredentials: Credential[] = [
  {
    label: 'Founder, SikhiWiki',
    detail: 'The most-used Sikh reference project on the internet.',
    icon: Sparkles,
  },
  {
    label: 'Creator, Sikh Kingdoms',
    detail: 'Producer of the immersive experience and the Digital Treasury.',
    icon: Crown,
  },
  {
    label: 'Professor of Entrepreneurship',
    detail: 'Ivey Business School and INSEAD.',
    icon: GraduationCap,
  },
  {
    label: 'Operator and builder',
    detail: 'Startup founder, engineer, and large-scale event producer.',
    icon: Building2,
  },
]

export type Benefit = {
  title: string
  body: string
  icon: LucideIcon
}

export const foundingBenefits: Benefit[] = [
  {
    title: 'Your name in the Court Roll',
    body: 'Founding Members are recorded permanently in the venue and in the Digital Treasury as the people who backed this before it existed.',
    icon: ScrollText,
  },
  {
    title: 'Opening season access',
    body: 'Two seats on a founders’ night at Illuminarium Toronto, plus first access to every Treasury Night in the season.',
    icon: Flame,
  },
  {
    title: 'A Founders’ Edition collection',
    body: 'A numbered Founders’ Edition set in the Digital Treasury, issued once and never re-minted.',
    icon: Gem,
  },
  {
    title: 'A seat at the table',
    body: 'A private quarterly call with Ash and the founding team on what is being built, what is working, and what we need next.',
    icon: MessageSquare,
  },
]

export const callAgenda: string[] = [
  'What we are building, in plain terms — the venue, the season, and the numbers behind it.',
  'Where you fit: patronage, introductions, corporate tables, or school sponsorship.',
  'What a Founding Member commitment actually looks like — and what it does not.',
  'Your questions, straight answers, and no follow-up pressure.',
]

export type Faq = {
  question: string
  answer: string
}

export const faqs: Faq[] = [
  {
    question: 'How much does becoming a Founding Member cost?',
    answer:
      'This is deliberately not a big-cheque program. Founding Membership is built to be reachable for working executives and professionals, and we set the level together on the call based on what makes sense for you. Introductions and expertise count as much as capital.',
  },
  {
    question: 'Who is this call actually with?',
    answer:
      'Ash Singh — founder of SikhiWiki, creator of Sikh Kingdoms, and Professor of Entrepreneurship at Ivey and INSEAD. Not a sales team. Twenty to thirty minutes, direct.',
  },
  {
    question: 'I am not in Toronto. Does this still make sense?',
    answer:
      'Yes. Toronto is the premiere, not the ceiling. Founding Members shape where the experience travels next, and the Digital Treasury reaches the diaspora wherever they are.',
  },
  {
    question: 'What do you need beyond money?',
    answer:
      'Rooms and relationships. Corporate partners, cultural institutions, school boards, and media. Many of our strongest Founding Members contributed an introduction long before they contributed a dollar.',
  },
]
